import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Mail } from "lucide-react";

export default function FollowUpEmailDialog({ quoteId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/quotes/${quoteId}/follow-up-draft`)
      .then((r) => {
        setTo(r.data.to || "");
        setSubject(r.data.subject || "");
        setMessage(r.data.message || "");
        setQuoteNumber(r.data.quote_number || "");
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, quoteId]);

  const send = async () => {
    if (!to.trim()) {
      toast.error("Enter the customer's email address.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/quotes/${quoteId}/follow-up-email`, { to: to.trim(), subject, message });
      toast.success(`Follow-up sent to ${to.trim()}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="followup-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[hsl(var(--primary))]" /> Delivery follow-up {quoteNumber && `· ${quoteNumber}`}
          </DialogTitle>
          <DialogDescription>Pre-drafted "how did we do?" email — sent from your connected Gmail.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="followup-to">To</Label>
              <Input id="followup-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="customer@company.com" data-testid="followup-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="followup-subject">Subject</Label>
              <Input id="followup-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="followup-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="followup-message">Message</Label>
              <Textarea id="followup-message" value={message} onChange={(e) => setMessage(e.target.value)}
                rows={9} className="text-sm" data-testid="followup-message-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="followup-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending || loading} data-testid="followup-send"
            className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}Send follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
